import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { ConfirmButton, Field, ImageField, MetaLine, SaveButton, StatusToggle, inputCls } from "../../components/admin/fields";
import { RichTextEditor } from "../../components/admin/RichTextEditor";

const toLocal = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16) : "");
const fromLocal = (v) => (v ? new Date(v).toISOString() : null);

const GalleryImagesField = ({ value, onChange }) => {
  const [busySlot, setBusySlot] = useState(null);
  const slots = Array.from({ length: 6 }, (_, i) => value[i] || { image: "", alt: "" });

  const upload = async (i, file) => {
    if (!file) return;
    setBusySlot(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      const next = slots.map((s, idx) => (idx === i ? { ...s, image: data.url } : s));
      onChange(next.filter((s) => s.image));
    } finally {
      setBusySlot(null);
    }
  };

  return (
    <Field label="Gallery (2 × 3 on the exhibition page)" hint="Up to six images. Each opens in a large overlay when clicked.">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="gallery-images-field">
        {slots.map((slot, i) => (
          <div key={i} className="border border-dashed border-charcoal p-2">
            {slot.image ? (
              <div className="relative">
                <img src={slot.image} alt={slot.alt || ""} className="aspect-[4/3] w-full object-cover" />
                <button
                  type="button"
                  data-testid={`gallery-slot-remove-${i}`}
                  onClick={() => onChange(slots.filter((s, idx) => idx !== i && s.image))}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-offwhite text-xs"
                  aria-label={`Remove gallery image ${i + 1}`}
                >
                  ×
                </button>
                <input
                  type="text"
                  value={slot.alt || ""}
                  onChange={(e) => onChange(slots.map((s, idx) => (idx === i ? { ...s, alt: e.target.value } : s)).filter((s2) => s2.image))}
                  placeholder="Alt text"
                  data-testid={`gallery-slot-alt-${i}`}
                  className="mt-2 w-full border border-charcoal bg-transparent px-2 py-1 text-xs font-light outline-none"
                />
              </div>
            ) : (
              <label className="flex aspect-[4/3] cursor-pointer items-center justify-center text-xs font-light opacity-60" data-testid={`gallery-slot-${i}`}>
                {busySlot === i ? "Uploading…" : "+ Add image"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(i, e.target.files?.[0])} />
              </label>
            )}
          </div>
        ))}
      </div>
    </Field>
  );
};

export default function ExhibitionEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/admin/exhibitions/${id}`).then((res) => setForm(res.data)).catch(() => setError("Exhibition not found"));
  }, [id]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  if (!form) return <p className="eyebrow">{error || "Loading…"}</p>;

  const set = (key) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [key]: v }));
    setDirty(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.put(`/admin/exhibitions/${id}`, form);
      setForm(data);
      setDirty(false);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-3xl space-y-8" data-testid="exhibition-edit-form">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="h-condensed text-4xl">Edit Exhibition</h1>
        <MetaLine by={form.updated_by} at={form.updated_at} />
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <StatusToggle value={form.status} onChange={set("status")} testid="exhibition-status-toggle" />
        <a
          href={`/exhibitions/${form.slug}?preview=1`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="preview-draft-button"
          className="link-arrow text-xs"
        >
          Preview draft
        </a>
        <label className="flex items-center gap-2 font-condensed text-xs font-semibold uppercase tracking-[0.14em]">
          <input type="checkbox" checked={!!form.show_in_recent} onChange={set("show_in_recent")} data-testid="show-in-recent-checkbox" className="h-4 w-4 accent-[#1E1E1E]" />
          Show in recent shows bar
        </label>
      </div>

      <Field label="Title">
        <input required value={form.title} onChange={set("title")} className={inputCls} data-testid="exhibition-title" />
      </Field>
      <Field label="Slug" hint={`Shown at /exhibitions/${form.slug || "…"}`}>
        <input required value={form.slug} onChange={set("slug")} className={inputCls} data-testid="exhibition-slug" />
      </Field>
      <div className="grid gap-6 sm:grid-cols-3">
        <Field label="Gallery">
          <input value={form.gallery || ""} onChange={set("gallery")} className={inputCls} data-testid="exhibition-gallery" />
        </Field>
        <Field label="City">
          <input value={form.city || ""} onChange={set("city")} className={inputCls} data-testid="exhibition-city" />
        </Field>
        <Field label="Country">
          <input value={form.country || ""} onChange={set("country")} className={inputCls} data-testid="exhibition-country" />
        </Field>
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Start date">
          <input type="datetime-local" value={toLocal(form.start_date)} onChange={(e) => { setForm((f) => ({ ...f, start_date: fromLocal(e.target.value) })); setDirty(true); }} className={inputCls} data-testid="exhibition-start-date" />
        </Field>
        <Field label="End date" hint="Leave empty for an ongoing show.">
          <input type="datetime-local" value={toLocal(form.end_date)} onChange={(e) => { setForm((f) => ({ ...f, end_date: fromLocal(e.target.value) })); setDirty(true); }} className={inputCls} data-testid="exhibition-end-date" />
        </Field>
      </div>
      <Field label="Date display override" hint='When filled, this text replaces the formatted dates everywhere — e.g. "October 2024 to July 2025". The Event schema still uses the real dates.'>
        <input value={form.date_display_override || ""} onChange={set("date_display_override")} className={inputCls} data-testid="exhibition-date-override" />
      </Field>

      <ImageField label="Hero image (full-viewport splash)" value={form.hero_image} onChange={set("hero_image")} alt={form.hero_alt} onAltChange={set("hero_alt")} focal={form.hero_focal} onFocalChange={set("hero_focal")} testid="exhibition-hero-image" />
      <ImageField label="Card image (arch crop, 4:5)" value={form.card_image} onChange={set("card_image")} alt={form.card_alt} onAltChange={set("card_alt")} focal={form.card_focal} onFocalChange={set("card_focal")} testid="exhibition-card-image" />

      <Field label="Body text">
        <RichTextEditor value={form.body} onChange={set("body")} testid="exhibition-body-editor" />
      </Field>

      <GalleryImagesField
        value={form.gallery_images || []}
        onChange={(v) => {
          setForm((f) => ({ ...f, gallery_images: v }));
          setDirty(true);
        }}
      />

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Button label (optional)">
          <input value={form.button_label || ""} onChange={set("button_label")} className={inputCls} data-testid="exhibition-button-label" />
        </Field>
        <Field label="Button URL (optional)">
          <input value={form.button_url || ""} onChange={set("button_url")} className={inputCls} data-testid="exhibition-button-url" />
        </Field>
      </div>

      <fieldset className="space-y-6 border border-charcoal p-5">
        <legend className="eyebrow px-2 text-[0.65rem]">SEO</legend>
        <Field label="SEO title" hint={`Falls back to "${form.title} at ${form.gallery || "Gallery"}, ${form.city || "City"} | Pantheon Art"`}>
          <input value={form.seo_title || ""} onChange={set("seo_title")} className={inputCls} data-testid="exhibition-seo-title" />
        </Field>
        <Field label="SEO description">
          <textarea rows={2} value={form.seo_description || ""} onChange={set("seo_description")} className={inputCls} data-testid="exhibition-seo-description" />
        </Field>
        <ImageField label="Share image (falls back to hero image)" value={form.share_image} onChange={set("share_image")} testid="exhibition-share-image" />
      </fieldset>

      <div className="flex flex-wrap items-center gap-6 border-t border-charcoal pt-6">
        <SaveButton busy={busy} dirty={dirty} />
        {dirty && <span className="text-xs font-light opacity-60">Unsaved changes</span>}
        {error && <p className="text-sm" role="alert" data-testid="exhibition-save-error">{error}</p>}
        <span className="flex-1" />
        <ConfirmButton
          label="Delete exhibition"
          testid="exhibition-delete-button"
          onConfirm={async () => {
            await api.delete(`/admin/exhibitions/${id}`);
            navigate("/admin/exhibitions");
          }}
        />
      </div>
    </form>
  );
}
